import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle,
  AlertCircle,
  Mic as MicIconLucide,
  Clock,
  ChevronDown,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

import {
  clearSessionId,
  clearGuestExamUser,
  clearExamId,
  clearGuestId,
  clearRetryAndExamStorage,
  getToken,
  clearToken,
} from "@/utils/storage";

import { checkSubscriptionAccess } from "@/utils/subscriptionCheck";
import {
  captureExamModeFromEntry,
  getExamMode,
  isDeliberateTrial,
  shouldRedirectAuthenticatedTrial,
} from "@/utils/examMode";
import { isValidlyAuthenticated } from "@/utils/auth";
import { isTask2MaintenanceEnabled } from "@/config/maintenance";
import Task2MaintenanceNotice from "@/components/task2/Task2MaintenanceNotice";

import mic from "../../public/images/mic.svg";
import user from "../../public/images/user.svg";
import chat from "../../public/images/chat.svg";

const MicTest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [micStatus, setMicStatus] = useState<
    "checking" | "detected" | "not-detected"
  >("checking");
  const [subscriptionActive, setSubscriptionActive] = useState(true);
  const [subscriptionChecking, setSubscriptionChecking] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    // MicTest is the public Trial entry: explicit signals win, otherwise a
    // fresh direct visit defaults to Trial. (Session is cleared below, so this
    // never reuses paid authority.)
    const mode = captureExamModeFromEntry({
      search: location.search,
      stateExamMode: (location.state as { examMode?: unknown } | null)?.examMode,
      defaultMode: "trial",
    });

    // PRODUCT RULE — Trial is guest-only. A validly authenticated user who lands
    // on the deliberate Trial entry is routed to their authenticated Dashboard
    // instead of starting a guest Trial. Paid auth / Supabase session is left
    // completely untouched (no clear, no signInAnonymously, no startGuestExam).
    if (
      shouldRedirectAuthenticatedTrial({
        mode,
        isValidlyAuthenticated: isValidlyAuthenticated(),
      })
    ) {
      navigate("/dashboard", { replace: true });
      return;
    }

    // Not validly authenticated but entering Trial: a stale/expired access_token
    // must NOT be treated as a paid session or contaminate guest websocket auth
    // (the original incident). Drop the dead paid credentials so the whole Trial
    // runs as a clean guest. (No-op for a genuinely logged-out visitor.)
    if (mode === "trial") {
      clearToken();
      localStorage.removeItem("user_data");
      localStorage.removeItem("sb-nluntjftnyzlxhatvvks-auth-token");
    }

    clearSessionId();
    clearGuestExamUser();
    clearExamId();
    clearGuestId();
    clearRetryAndExamStorage();

    const checkMic = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        setMicStatus("detected");
      } catch {
        setMicStatus("not-detected");
      }
    };

    checkMic();

    const checkSubscription = async () => {
      // Deliberate Trial (guest-only; validly authenticated users were already
      // redirected to Dashboard above) must not be gated by paid entitlement,
      // and must not call get_user_subscriptions.
      const deliberateTrial = isDeliberateTrial();
      const token = getToken();

      if (deliberateTrial) {
        const result = await checkSubscriptionAccess({ deliberateTrial: true });
        setSubscriptionActive(result.hasAccess);
        setSubscriptionChecking(false);
        return;
      }

      // If no token, skip subscription check (guest users or unauthenticated)
      if (!token) {
        console.log("ℹ️ No token found - skipping subscription check (guest/unauthenticated user)");
        setSubscriptionActive(true);
        setSubscriptionChecking(false);
        return;
      }

      // Only check subscription for authenticated users with token
      try {
        console.log("🔍 Token found - checking subscription for authenticated user");
        const result = await checkSubscriptionAccess();
        setSubscriptionActive(result.hasAccess);
      } catch (error) {
        console.error("Error checking subscription:", error);
        // On error, allow access to avoid blocking users
        setSubscriptionActive(true);
      } finally {
        setSubscriptionChecking(false);
      }
    };
    checkSubscription();
  }, [location.search, location.state]);

  const handleStartExam = async () => {
    if (isTask2MaintenanceEnabled()) {
      return;
    }

    // 🔊 UNLOCK AUDIO CONTEXT FOR IOS
    try {
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (AC) {
        let ctx = (window as any).__appAudioContext;
        if (!ctx) {
          ctx = new AC();
          (window as any).__appAudioContext = ctx;
        }
        
        // Always try to resume
        if (ctx.state === 'suspended') {
          await ctx.resume();
        }
        
        // Play silent buffer to force unlock
        const buffer = ctx.createBuffer(1, 1, 22050);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.start(0);
        console.log('🔊 Audio context unlocked on Start Exam click, state:', ctx.state);
        
        // Short delay to ensure state propagates
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    } catch (e) {
      console.warn('Failed to unlock audio context:', e);
    }

    // ✅ GA/GTM event: exam started (Mic Test -> Task 1)
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      event: "exam_start",
      funnel_step: "mic_test",
      next_path: "/task-one",
    });

    navigate("/task-one", { state: { examMode: getExamMode() } });
  };

  return (
    <div className="w-full flex justify-center px-4 pt-[56px] pb-[80px]">
      <div className="w-full max-w-[880px] space-y-12">
        {/* MAIN INTRO CARD */}
        <Card className="rounded-2xl border border-[#E5EAF1] bg-white">
          <CardContent className="p-4 sm:p-8 space-y-6 sm:space-y-8">
            <div className="text-center space-y-3">
              <h1 className="text-[28px] font-bold text-[#1C1F26] leading-[36px]">
                Ready to start your speaking exam?
              </h1>
              <p className="text-[15px] text-[#616875] leading-[24px] max-w-[580px] mx-auto">
                This exam takes approximately 12 minutes and follows the official TCF Canada structure. 
                Find a quiet space, ensure your microphone is working, and get comfortable - background noise can affect your recording.
              </p>
            </div>

            {/* Mic Status */}
            <div className="flex flex-col items-center space-y-3">
              <div
                className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                  micStatus === "detected"
                    ? "bg-green-100"
                    : micStatus === "not-detected"
                    ? "bg-red-100"
                    : "bg-gray-100"
                }`}
              >
                <MicIconLucide
                  className={`w-7 h-7 ${
                    micStatus === "detected"
                      ? "text-green-600"
                      : micStatus === "not-detected"
                      ? "text-red-600"
                      : "text-[#8B7AF5]"
                  }`}
                />
              </div>

              {micStatus === "checking" && (
                <p className="text-sm text-gray-600">Checking microphone…</p>
              )}

              {micStatus === "detected" && (
                <div className="flex items-center text-[#0E9F6E] gap-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    Microphone detected and ready.
                  </span>
                </div>
              )}

              {micStatus === "not-detected" && (
                <div className="flex items-center text-[#EF4444] gap-2">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    We couldn’t detect your microphone. Please allow mic access.
                  </span>
                </div>
              )}

              {!subscriptionActive && !subscriptionChecking && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg w-full">
                  <p className="text-sm text-red-800 font-medium text-center">
                    ⚠️ Your subscription has expired. Please renew your
                    subscription to access the exam.
                  </p>
                </div>
              )}
            </div>

            {/* Tips Card */}
            <Card className="bg-[#F8F9FB] border border-[#E5EAF1] rounded-xl">
              <CardContent className="p-3 sm:p-5 space-y-3">
                <div className="flex items-start gap-2">
                  <Clock className="w-5 h-5 text-[#005FCC] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#1C1F26] leading-[22px]">
                    <strong>The exam takes 12 minutes:</strong> Task 1 (2 min) → Task 2 (5.5 min) → Task 3 (4.5 min). Once you start, complete all three tasks without stopping.
                  </p>
                </div>
            
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-[#0E9F6E] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#1C1F26] leading-[22px]">
                    <strong>Audio instructions play during each task.</strong> Listen first, then speak when the recording timer starts.
                  </p>
                </div>
            
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-[#0E9F6E] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#1C1F26] leading-[22px]">
                    Your performance is evaluated on fluency, grammar, vocabulary, pronunciation, and comprehension across all three speaking tasks.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Best Setup Recommendation */}
            <div className="bg-[#F0F7FF] border border-[#BFDBFE] rounded-lg p-3 sm:p-4">
              <p className="text-sm text-[#1E40AF] leading-[22px] text-center">
                💡 <strong>Best experience:</strong> Desktop/laptop with Chrome browser and headphones for clearer audio instructions.
              </p>
            </div>
            
            {/* First Time Guidance + Feedback Info */}
            <div className="text-center space-y-3">
              <button 
                onClick={() => {
                  const section = document.getElementById("exam-tasks-section");
                  if (section) {
                    const offsetPosition = section.getBoundingClientRect().top + window.pageYOffset - 80;
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: "smooth"
                    });
                  }
                }}
                className="inline-flex items-center justify-center gap-2 text-sm text-[#005FCC] hover:text-[#0049A8] font-medium transition-colors group"
              >
                <span className="hidden sm:inline">First time? Review what happens in each task below</span>
                <span className="sm:hidden">Review what happens in each task</span>
                <ChevronDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform flex-shrink-0" />
              </button>
              
              <p className="text-xs text-[#616875] max-w-[480px] mx-auto">
                After your exam, you'll see your final score. Full detailed feedback (with grammar corrections, vocabulary suggestions, and improvement tips) is available with a subscription.
              </p>
            </div>

            {isTask2MaintenanceEnabled() ? (
              <Task2MaintenanceNotice variant="fullExam" className="mt-2" />
            ) : null}

            <div className="text-center pt-2">
              <Button
                onClick={handleStartExam}
                disabled={
                  micStatus !== "detected" ||
                  !subscriptionActive ||
                  subscriptionChecking ||
                  isTask2MaintenanceEnabled()
                }
                className="h-11 px-7 rounded-[10px] bg-[#005FCC] hover:bg-[#0049A8] text-white text-[14px] font-medium transition-colors disabled:opacity-50"
              >
                {isTask2MaintenanceEnabled()
                  ? "Temporarily unavailable"
                  : "Start My Exam"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* HOW THE EXAM WORKS */}
        <div className="space-y-6" id="exam-tasks-section">
          <h2 className="text-xl font-semibold text-[#1C1F26]">
            What to expect in each task
          </h2>

          <Accordion
            type="single"
            collapsible
            className="w-full space-y-3"
            defaultValue={typeof window !== 'undefined' && window.innerWidth >= 640 ? "task1" : undefined}
          >
            {/* ========== TASK 1 ========== */}
            <AccordionItem
              value="task1"
              className="w-full rounded-xl border border-[#0000000F] bg-[#F6F6F6] hover:bg-[#F3F4F6] transition-colors px-3 sm:px-5 shadow-sm"
            >
              <AccordionTrigger className="py-4 text-base font-medium text-[#1C1F26] hover:no-underline">
                <div className="flex items-center gap-3">
                  <img src={mic} className="w-5 h-5" alt="Microphone" />
                  <span>Task 1 — Self introduction</span>
                </div>
              </AccordionTrigger>
          
              <AccordionContent className="pb-5 pt-1">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#6B7280]" />
                    <p className="text-xs text-[#6B7280] font-medium">2 minutes • No preparation time</p>
                  </div>
              
                  <div className="text-sm text-[#4B5563] leading-[22px] space-y-3">
                    <p>
                      <strong>What happens:</strong> You'll hear a brief introduction in French, then speak for 2 minutes about yourself. There may be follow-up questions. Just introduce yourself naturally as if meeting someone for the first time.
                    </p>
              
                    <div className="bg-white rounded-lg p-3 border border-[#E5EAF1]">
                      <p className="text-xs text-[#6B7280] font-medium mb-2">Example prompt you'll hear:</p>
                      <p className="text-sm text-[#1C1F26] mb-2">
                        "Parlez-moi de vous"
                      </p>
                      <p className="text-xs text-[#616875] italic">
                        (Talk abour yourself)
                      </p>
                    </div>
              
                    <p>
                      <strong>Strategy:</strong> This is your warm-up. Speak continuously for 2 minutes covering: who you are, your family/background, work or studies, hobbies, and future plans. Don't worry about perfection. Focus on sounding natural, comfortable, and maintaining steady rhythm.
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          
            {/* ========== TASK 2 ========== */}
            <AccordionItem
              value="task2"
              className="w-full rounded-xl border border-[#0000000F] bg-[#F6F6F6] hover:bg-[#F3F4F6] transition-colors px-3 sm:px-5 shadow-sm"
            >
              <AccordionTrigger className="py-4 text-base font-medium text-[#1C1F26] hover:no-underline">
                <div className="flex items-center gap-3">
                  <img src={user} className="w-5 h-5" alt="User" />
                  <span>Task 2 — Roleplay questions</span>
                </div>
              </AccordionTrigger>
          
              <AccordionContent className="pb-5 pt-1">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#6B7280]" />
                    <p className="text-xs text-[#6B7280] font-medium">5.5 minutes total • 2 min preparation + 3.5 min speaking</p>
                  </div>
              
                  <div className="text-sm text-[#4B5563] leading-[22px] space-y-3">
                    <p>
                      <strong>What happens:</strong> You'll see a written situation on your screen (asking for information, arranging a service, or solving a problem). You'll have 2 minutes to read and prepare your approach before the recording starts.
                    </p>
              
                    <div className="bg-white rounded-lg p-3 border border-[#E5EAF1] space-y-3">
                      <div>
                        <p className="text-xs text-[#6B7280] font-medium mb-2">Example scenario (shown in French):</p>
                        <p className="text-sm text-[#1C1F26] mb-2">
                          <strong>Situation:</strong> "Vous voulez organiser un voyage de fin de semaine avec vos amis. Vous téléphonez à une agence de voyages pour obtenir des renseignements."
                        </p>
                        <p className="text-xs text-[#616875] italic">
                          (You want to organize a weekend trip with friends. You call a travel agency to get information.)
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-xs text-[#6B7280] font-medium mb-2">How you might structure your response:</p>
                        <div className="text-sm text-[#4B5563] space-y-2">
                          <p><strong>Introduction:</strong> "Bonjour, j'aimerais organiser un voyage de fin de semaine avec mes amis..."</p>
                          <div>
                            <p className="font-medium mb-1">Questions (8-12 total):</p>
                            <ul className="ml-4 space-y-0.5 text-[13px]">
                              <li>• "Quelles destinations proposez-vous pour un groupe de 6 personnes?"</li>
                              <li>• "Quel est le prix par personne?"</li>
                              <li>• "Est-ce que le transport est inclus?"</li>
                              <li>• "Quelles activités sont disponibles sur place?"</li>
                              <li>• "..."</li>
                            </ul>
                          </div>
                          <p><strong>Closing:</strong> "Merci beaucoup pour toutes ces informations. Je vais en discuter avec mes amis."</p>
                        </div>
                      </div>
                    </div>
              
                    <p>
                      <strong>Strategy:</strong> Structure your response with a clear introduction, 8-12 relevant questions based on the situation, and a polite closing. Speak naturally and react to imagined responses. This shows you can maintain a coherent conversation.
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          
            {/* ========== TASK 3 ========== */}
            <AccordionItem
              value="task3"
              className="w-full rounded-xl border border-[#0000000F] bg-[#F6F6F6] hover:bg-[#F3F4F6] transition-colors px-3 sm:px-5 shadow-sm"
            >
              <AccordionTrigger className="py-4 text-base font-medium text-[#1C1F26] hover:no-underline">
                <div className="flex items-center gap-3">
                  <img src={chat} className="w-5 h-5" alt="Chat" />
                  <span>Task 3 — Opinion and argument</span>
                </div>
              </AccordionTrigger>
          
              <AccordionContent className="pb-5 pt-1">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#6B7280]" />
                    <p className="text-xs text-[#6B7280] font-medium">4.5 minutes • No preparation time</p>
                  </div>
              
                  <div className="text-sm text-[#4B5563] leading-[22px] space-y-3">
                    <p>
                      <strong>What happens:</strong> You'll hear a question about a social topic or situation. You must immediately express your opinion and explain your reasoning. No preparation time. Just speak naturally and stay focused.
                    </p>
              
                    <div className="bg-white rounded-lg p-3 border border-[#E5EAF1]">
                      <p className="text-xs text-[#6B7280] font-medium mb-2">Example question (in French):</p>
                      <p className="text-sm text-[#1C1F26] mb-2">
                        "Certaines personnes pensent que les réseaux sociaux ont un impact négatif sur la société. Quel est votre avis? Expliquez pourquoi."
                      </p>
                      <p className="text-xs text-[#616875] italic">
                        (Some people think social media has a negative impact on society. What is your opinion? Explain why.)
                      </p>
                    </div>
              
                    <p>
                      <strong>Strategy:</strong> This is where you showcase your strongest French. State your position clearly, give 2-3 solid reasons with examples, use connecting phrases ("D'abord... Ensuite... En conclusion..."), and maintain a confident, steady rhythm throughout. Quality matters more than speed.
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          
          </Accordion>
          {/* Final Encouragement */}
          <Card className="bg-gradient-to-br from-[#F0F7FF] to-[#E0F2FE] border border-[#BFDBFE] rounded-xl">
            <CardContent className="p-4 sm:p-6 text-center">
              <p className="text-sm text-[#1E40AF] leading-[22px]">
                <strong>Remember:</strong> The exam follows the real TCF Canada format used for Canadian immigration. 
                Speak clearly, stay calm, and focus on completing each task fully. You've got this!
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MicTest;


